import { useMediaQuery } from '@finn/design-system/helpers/media';
import { useIsScrolledUp } from '@finn/platform-modules';
import { Features, useIsABVariant } from '@finn/ua-featureflags';
import {
  ModalKey,
  useIsAnyModalOpen,
  useIsModalOpen,
  useOpenModal,
} from '@finn/ua-modals';
import { interactionTrackingEvent, TrackingEventName } from '@finn/ua-tracking';
import { cn } from '@finn/ui-utils';
import { ReactNode, useCallback, useContext, useMemo } from 'react';

import { useDiscoveryConfig } from '../../core/data/MetadataProvider';
import { GenericVehicleDetails } from '../../core/types';
import {
  DirectCheckout,
  DirectCheckoutEligibility,
  useConfigureStore,
  useInitializeConfigureStore,
} from '../../pdp-management';
import { EmissionModalContext, VehicleContext } from '../../product-cards';
import { PartialCartInfo } from '../../product-pricing';
import { AccordionBenefits } from './AccordionBenefits';
import { AvailabilityElements } from './AvailabilityElements';
import { ClickableVehiclesBenefits } from './ClickableVehiclesBenefits';
import { ComingSoonSection } from './ComingSoonSection';
import { ContinueButton, ContinueButtonLocation } from './ContinueButton';
import { ImageGallery } from './ImageGallery/ImageGallery';
import { isPreloved, PreLovedInfo } from './PreLovedModal';
import { StickyBar } from './StickyBar';
import { SubscriptionPrice } from './SubscriptionPrice';
import { TrustPilotRating, TrustpilotStyle } from './TrustPilotRating';
import { VehicleMainDetails } from './VehicleMainDetails';
import { VehiclesColor } from './VehiclesColor';

export const allowedAccordingToPolicy = (
  policy: ReturnType<typeof useDiscoveryConfig>['carPolicy'],
  vehicle: GenericVehicleDetails
) => {
  if (!policy) {
    return true;
  }

  const violatesBrandPolicy =
    policy?.brands?.length && !policy.brands.includes(vehicle.brand.id);
  const violatesFuelTypePolicy =
    policy?.fuelTypes?.length && !policy.fuelTypes.includes(vehicle.fuel);
  const violatesCarTypePolicy =
    policy?.carTypes?.length && !policy.carTypes.includes(vehicle.cartype);

  const banned =
    violatesBrandPolicy || violatesFuelTypePolicy || violatesCarTypePolicy;

  return !banned;
};

export const ProductDetails = <T,>({
  pageData,
  onAddToCart,
  isLoading,
  triggerDevModal,
  directCheckoutEligibility,
  renderPromoSection,
  closedUserGroupId,
  cartInfo,
  directCheckout,
  contactId,
  onCheckout,
  partnerVoucherBanner,
}: {
  onAddToCart: () => void;
  isLoading?: boolean;
  pageData: T;
  hideBreadCrumbs?: boolean;
  triggerDevModal?: () => void;
  renderPromoSection?: () => ReactNode;
  closedUserGroupId?: string;
  cartInfo?: PartialCartInfo;
  directCheckout?: DirectCheckout;
  directCheckoutEligibility: DirectCheckoutEligibility;
  contactId: string;
  priceAreaChildren?: ReactNode;
  onCheckout?: ({ id, term, kilometerPackage, downPaymentAmount }) => void;
  partnerVoucherBanner?: ReactNode;
}) => {
  const is36MonthsEnabled = useIsABVariant(Features.ExpPCP36Months);
  const isMobile = useMediaQuery('sm');
  const [targetRef, isScrolledUp] = useIsScrolledUp<HTMLDivElement>();
  const { vehicle } = useContext(VehicleContext);
  const { modalOpen } = useContext(EmissionModalContext);
  const {
    onContinue,
    isLoading: isLoadingFromPDPConfigModal,
    add36Term,
  } = useConfigureStore();
  const isPDPConfigModalOpen = useIsModalOpen(ModalKey.CONFIGURE_VEHICLE);
  const { carPolicy, goToNextCheckoutStep } = useDiscoveryConfig();
  const isAvailable =
    !!vehicle.availability && allowedAccordingToPolicy(carPolicy, vehicle);
  const pictures = vehicle.pictures.map((pic) => pic?.url).filter(Boolean);
  const disclaimerText = (pageData as any).metadata.content
    ?.bmw_rims_disclaimer;
  const isAnyModalOpen = useIsAnyModalOpen();
  const isExpPDPBenefits = useIsABVariant(Features.ExpPDPBenefits);

  const shouldShowStickyTopBar = isScrolledUp && !modalOpen && !isAnyModalOpen;

  const shouldShowDisclaimer = useMemo(() => {
    const { brand, purchasing_deal_name } = vehicle;

    return brand.id === 'BMW' && purchasing_deal_name?.includes('L');
  }, [vehicle]);

  useInitializeConfigureStore(vehicle, directCheckoutEligibility, contactId, {
    onCheckout,
  });

  const handleAddToCart = useCallback(() => {
    if (is36MonthsEnabled) {
      add36Term(vehicle);
    }

    onAddToCart();
  }, [add36Term, is36MonthsEnabled, onAddToCart, vehicle]);

  const renderContinueButton = useCallback(
    (location: ContinueButtonLocation) => (
      <ContinueButton
        onClick={handleAddToCart}
        isLoading={isLoading}
        location={location}
        buttonClassName={'sm:p-4'}
      />
    ),
    [isLoading, handleAddToCart]
  );

  const renderContinueToCheckoutButton = useMemo(
    () => (
      <ContinueButton
        onClick={() => onContinue(directCheckout, goToNextCheckoutStep)}
        isLoading={isLoadingFromPDPConfigModal}
        location={ContinueButtonLocation.STICKY}
      />
    ),
    [isLoadingFromPDPConfigModal, onContinue]
  );

  const isPreLoved = isPreloved(vehicle);

  const stickySubmitButton = useCallback(
    () =>
      isPDPConfigModalOpen
        ? renderContinueToCheckoutButton
        : renderContinueButton(ContinueButtonLocation.STICKY),
    [isPDPConfigModalOpen, renderContinueButton, renderContinueToCheckoutButton]
  );

  const mainSubmitButton = useCallback(
    () => renderContinueButton(ContinueButtonLocation.MAIN),
    [renderContinueButton]
  );
  const openModal = useOpenModal();

  const handleMoreInformationClick = () => {
    interactionTrackingEvent(TrackingEventName.MODAL_OPENED, {
      name: 'carinfo',
    });
    openModal(ModalKey.PDP_CAR_INFO);
  };

  return (
    <>
      <StickyBar
        vehicle={vehicle}
        show={shouldShowStickyTopBar}
        isAvailable={isAvailable}
        renderSubmitButton={stickySubmitButton}
        position={isMobile ? 'bottom' : 'top'}
        closedUserGroupId={closedUserGroupId}
        cartInfo={cartInfo}
      />
      <div className="container">
        {partnerVoucherBanner}
        <section className="m-auto flex flex-wrap">
          <div
            className={cn(
              'max-w-full grow-0 basis-full md:pr-10',
              `md:max-w-[calc(100%*2/3)] md:basis-[calc(100%*2/3)]`
            )}
          >
            {renderPromoSection?.()}
            <ImageGallery
              imageUrls={pictures}
              showDisclaimer={shouldShowDisclaimer}
              disclaimerText={disclaimerText}
            />
          </div>
          <div
            className={cn(
              'relative m-auto max-w-full grow pt-6 sm:max-w-full sm:basis-full md:m-0 md:pt-0',
              `md:max-w-[calc(100%/3)] md:basis-[calc(100%/3)]`
            )}
          >
            <div className="sticky top-2">
              <div className="flex flex-col gap-6">
                <VehicleMainDetails
                  vehicle={vehicle}
                  handleMoreInformationClick={handleMoreInformationClick}
                  closedUserGroupId={closedUserGroupId}
                  cartInfo={cartInfo}
                  triggerDevModal={triggerDevModal}
                />
                <div>
                  <VehiclesColor vehicle={vehicle} />
                </div>
                {isAvailable && (
                  <>
                    <AvailabilityElements vehicle={vehicle} />
                    {isPreLoved && <PreLovedInfo pdpData={pageData} />}
                    <div className="flex flex-col gap-4" data-cy="price-area">
                      <SubscriptionPrice
                        closedUserGroupId={closedUserGroupId}
                        cartInfo={cartInfo}
                      />
                      {mainSubmitButton()}
                    </div>
                  </>
                )}
              </div>

              <div ref={targetRef} />

              {!isAvailable && (
                <div className="mt-6 flex flex-col" data-cy="price-area">
                  <SubscriptionPrice
                    closedUserGroupId={closedUserGroupId}
                    cartInfo={cartInfo}
                  />
                  <ComingSoonSection onAddToCart={onAddToCart} />
                </div>
              )}
              {isExpPDPBenefits && <AccordionBenefits />}

              <TrustPilotRating
                style={TrustpilotStyle.PDP}
                desktopClassName={cn(
                  'mt-8 mb-2 -left-[60px] max-w-fit',
                  isExpPDPBenefits && 'mt-6'
                )}
                mobileClassName={cn(
                  'mt-8 mb-2 -left-[60px] max-w-fit',
                  isExpPDPBenefits && 'mt-6'
                )}
                mobileHeight="20px"
                desktopHeight="20px"
              />

              {!isExpPDPBenefits && <ClickableVehiclesBenefits />}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};
